import { store } from '../store/store';
import { refreshAccessToken } from './api';
import {
  fetchNotifications,
  notificationAdded,
  notificationsSnapshot,
  setNotificationsConnection,
} from '../store/actions/notifications';
import type { NotificationEnvelope } from '@/features/notifications/types';

const TOPIC = 'notifications';
const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 1_000;
const RATE_LIMIT_DELAY = 60_000;

const CONTROL_TYPES = new Set([
  'connection.established',
  'subscribed',
  'subscription.denied',
  'unsubscribed',
  'token.refreshed',
  'ping',
  'sync.fallback',
]);

let socket: WebSocket | null = null;
let manualClose = false;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let lastSeq = 0;

// Generic (resource-scoped) topic subscriptions sharing this one connection,
// e.g. `pdf:{id}`. The `notifications` topic is handled separately (persistent,
// dispatched to Redux). Handlers receive the payload (data for events, snapshot
// on subscribe).
type TopicHandler = (payload: Record<string, unknown>) => void;
const topicHandlers = new Map<string, TopicHandler>();

const resolveWsUrl = (): string | null => {
  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicit) return explicit;

  // Dev: go through the vite `/ws` proxy on the same origin to avoid CORS.
  const base =
    import.meta.env.DEV && typeof window !== 'undefined'
      ? window.location.origin
      : (import.meta.env.VITE_BASE_API as string | undefined);
  if (!base) return null;

  try {
    const url = new URL(base);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/ws/';
    url.search = '';
    return url.toString();
  } catch {
    return null;
  }
};

const send = (payload: Record<string, unknown>): void => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
  }
};

const clearReconnectTimer = (): void => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const scheduleReconnect = (delay?: number): void => {
  if (manualClose) return;
  clearReconnectTimer();
  const backoff =
    delay ?? Math.min(BASE_RECONNECT_DELAY * 2 ** reconnectAttempts, MAX_RECONNECT_DELAY);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => connect(), backoff);
};

const handleMessage = (event: MessageEvent): void => {
  let message: any;
  try {
    message = JSON.parse(event.data);
  } catch {
    return;
  }

  const type = message?.type as string | undefined;

  if (type === 'ping') {
    send({ type: 'pong', time: message.time });
    return;
  }

  if (type === 'subscribed') {
    if (message.topic === TOPIC) {
      const snapshot = message.snapshot;
      if (snapshot) {
        lastSeq = Math.max(lastSeq, snapshot.lastSeq ?? 0);
        store.dispatch(notificationsSnapshot(snapshot));
      }
    } else {
      const handler = topicHandlers.get(message.topic);
      if (handler && message.snapshot) handler(message.snapshot);
    }
    return;
  }

  if (type === 'sync.fallback') {
    fetchNotifications()(store.dispatch);
    return;
  }

  // Non-control message with an id → a data envelope for some topic.
  if (!type || !CONTROL_TYPES.has(type)) {
    if (!message?.id) return;
    if (message.topic === TOPIC) {
      const envelope = message as NotificationEnvelope;
      lastSeq = Math.max(lastSeq, envelope.seq ?? 0);
      store.dispatch(notificationAdded(envelope));
      return;
    }
    const handler = topicHandlers.get(message.topic);
    if (handler) handler(message.data ?? {});
  }
};

const handleOpen = (): void => {
  reconnectAttempts = 0;
  store.dispatch(setNotificationsConnection('open'));
  send({ type: 'subscribe', topic: TOPIC });
  if (lastSeq > 0) {
    send({ type: 'sync', topic: TOPIC, lastSeq });
  }
  // Re-subscribe resource-scoped topics after a (re)connect.
  topicHandlers.forEach((_handler, topic) => send({ type: 'subscribe', topic }));
};

const handleClose = (event: CloseEvent): void => {
  socket = null;
  store.dispatch(setNotificationsConnection('closed'));

  if (manualClose) return;

  // 4401: token invalid → refresh via REST, then reconnect.
  if (event.code === 4401) {
    refreshAccessToken()
      .then(() => scheduleReconnect(BASE_RECONNECT_DELAY))
      .catch(() => {
        /* refresh failed → auth interceptor handles logout */
      });
    return;
  }

  // 4429: rate limited (too many connections) → back off longer.
  if (event.code === 4429) {
    scheduleReconnect(RATE_LIMIT_DELAY);
    return;
  }

  scheduleReconnect();
};

export const connect = (): void => {
  if (typeof WebSocket === 'undefined') return;
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  const token = store.getState().auth.access_token;
  const baseUrl = resolveWsUrl();
  if (!token || !baseUrl) return;

  manualClose = false;
  clearReconnectTimer();
  store.dispatch(setNotificationsConnection('connecting'));

  const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  socket = new WebSocket(url);
  socket.onopen = handleOpen;
  socket.onmessage = handleMessage;
  socket.onclose = handleClose;
  socket.onerror = () => {
    // onclose fires afterwards and drives reconnection.
  };
};

/**
 * Subscribe to a resource-scoped topic (e.g. `pdf:{id}`) over the shared gateway
 * connection. `handler` is invoked with the payload on the initial snapshot and
 * on every subsequent event. Safe to call before the socket is open — the
 * subscription is (re)sent on connect. Pair with `unsubscribeTopic`.
 */
export const subscribeTopic = (topic: string, handler: TopicHandler): void => {
  topicHandlers.set(topic, handler);
  send({ type: 'subscribe', topic });
};

export const unsubscribeTopic = (topic: string): void => {
  if (!topicHandlers.has(topic)) return;
  topicHandlers.delete(topic);
  send({ type: 'unsubscribe', topic });
};

export const disconnect = (): void => {
  manualClose = true;
  clearReconnectTimer();
  reconnectAttempts = 0;
  lastSeq = 0;
  topicHandlers.clear();
  if (socket) {
    socket.onclose = null;
    socket.close();
    socket = null;
  }
  store.dispatch(setNotificationsConnection('idle'));
};
